/**
 * Single table column filter + sort menu used across the app.
 * All / Clear live in the header row; footer uses Cancel + Apply (or custom actions).
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Menu,
  type MenuProps,
  Tabs,
  Tab,
  Button,
  Divider,
  TextField,
  Typography,
  alpha,
  FormControl,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  IconButton,
} from '@mui/material';
import {
  ArrowDropUp as ArrowDropUpIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { SEARCE_COLORS } from '../../theme';
import {
  columnFilterCaptionSx,
  columnFilterEmptyTypographySx,
  columnFilterFooterPrimaryButtonSx,
  columnFilterFooterSecondaryButtonSx,
  columnFilterHeaderActionButtonSx,
  columnFilterMenuListSx,
  columnFilterMenuPaperSx,
  columnFilterOptionRowTypographySx,
  columnFilterSearchFieldSx,
  columnFilterSectionTitleSx,
  columnFilterSortAscDescButtonSx,
  columnFilterSortHintSx,
  columnFilterTabSx,
  columnFilterTabsSx,
} from './theme/columnFilterMenuTheme';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

export type SortDirection = 'asc' | 'desc';
export type NumericRangeInputStyle = 'minMax' | 'greaterLess';
export type SortPanelMode = 'ascDesc' | 'columnSelect';

export interface UnifiedColumnSortFilterProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  anchorOrigin?: MenuProps['anchorOrigin'];
  transformOrigin?: MenuProps['transformOrigin'];
  columnKey: string;
  title: string;
  /** Current column's sort field when using asc/desc panel */
  sortField?: string;
  isSortable: boolean;
  onSort: (field: string, order: SortDirection) => void;
  currentSortField?: string | null;
  currentSortOrder?: string;
  filterOptions?: string[];
  selectedFilterValues?: string[];
  /** Committed search string for this column when the menu opens (in-menu search is draft until Apply). */
  initialFilterSearchTerm?: string;
  onFilterApply?: (values: string[], meta?: { searchTerm?: string }) => void;
  onRangeFilterApply?: (min: number | null, max: number | null) => void;
  /** When both set, min/max fields are controlled by parent (live draft), e.g. DRI numeric menus. */
  onRangeMinInputChange?: (value: number | null) => void;
  onRangeMaxInputChange?: (value: number | null) => void;
  onDateFilterApply?: (startDate: Date | null, endDate: Date | null) => void;
  enableSearch?: boolean;
  isRangeFilter?: boolean;
  isDateFilter?: boolean;
  numericRangeInputStyle?: NumericRangeInputStyle;
  dateFilter?: { startDate: Date | null; endDate: Date | null };
  minValue?: number | null;
  maxValue?: number | null;
  rangeMin?: number;
  rangeMax?: number;
  /** Shown above min/max fields when `isRangeFilter` */
  numericRangeHint?: string;
  children?: React.ReactNode;
  /** Replaces built-in filter body (keeps standard header + footer unless disabled). */
  customFilterContent?: React.ReactNode;
  /** Skip "Filter {title}" row and top divider; use with `customFilterContent` for fully custom layouts. */
  hideStandardFilterHeader?: boolean;
  /** Hide All/Clear text buttons in header (still shows title + optional header icon). */
  omitAllClearHeader?: boolean;
  /** When true, filter tab is shown even if option list is empty (e.g. bench). */
  forceShowFilterTab?: boolean;
  /** Parent owns checkbox + search state (Bench / Sentiment-style). */
  textFilterControlled?: boolean;
  controlledSearchTerm?: string;
  onControlledSearchChange?: (value: string) => void;
  controlledSelectedValues?: string[];
  onToggleControlledValue?: (value: string) => void;
  onControlledSelectAll?: () => void;
  onControlledClearSelections?: () => void;
  /** Optional X in header (DRI-style) */
  showHeaderClearIcon?: boolean;
  onHeaderClearFilter?: () => void;
  /** Controlled tabs (0 = Filter, 1 = Sort) */
  controlledTabValue?: number;
  onControlledTabChange?: (event: React.SyntheticEvent, value: number) => void;
  sortPanelMode?: SortPanelMode;
  sortableColumns?: Array<{ id: string; label: string }>;
  sortColumnSelectValue?: string | null;
  onSortColumnSelectChange?: (id: string | null) => void;
  sortDirection?: SortDirection;
  onSortDirectionChange?: (dir: SortDirection) => void;
  primaryFooterLabel?: string;
  secondaryFooterLabel?: string;
  /** Use `outlined` for DRI-style Close/Clear next to contained Apply */
  secondaryFooterVariant?: 'text' | 'outlined';
  showSelectionCountInApply?: boolean;
  /** Overrides default primary footer (Apply). */
  footerPrimaryAction?: () => void;
  /** Overrides default secondary footer (Cancel → onClose). */
  footerSecondaryAction?: () => void;
}

function sanitizeOptions(options: string[] | undefined): string[] {
  return (options || []).filter((o): o is string => o != null && String(o).trim() !== '');
}

const UnifiedColumnSortFilter: React.FC<UnifiedColumnSortFilterProps> = ({
  anchorEl,
  open,
  onClose,
  anchorOrigin,
  transformOrigin,
  sortField,
  isSortable,
  onSort,
  currentSortField,
  currentSortOrder,
  filterOptions,
  selectedFilterValues,
  initialFilterSearchTerm = '',
  onFilterApply,
  onRangeFilterApply,
  onRangeMinInputChange,
  onRangeMaxInputChange,
  onDateFilterApply,
  title,
  enableSearch = false,
  isRangeFilter = false,
  isDateFilter = false,
  numericRangeInputStyle = 'minMax',
  dateFilter,
  minValue,
  maxValue,
  rangeMin = 0,
  rangeMax = 1000000,
  numericRangeHint,
  children,
  customFilterContent,
  hideStandardFilterHeader = false,
  omitAllClearHeader = false,
  forceShowFilterTab = false,
  textFilterControlled = false,
  controlledSearchTerm = '',
  onControlledSearchChange,
  controlledSelectedValues = [],
  onToggleControlledValue,
  onControlledSelectAll,
  onControlledClearSelections,
  showHeaderClearIcon = false,
  onHeaderClearFilter,
  controlledTabValue,
  onControlledTabChange,
  sortPanelMode = 'ascDesc',
  sortableColumns = [],
  sortColumnSelectValue,
  onSortColumnSelectChange,
  sortDirection = 'asc',
  onSortDirectionChange,
  primaryFooterLabel = 'Apply',
  secondaryFooterLabel = 'Cancel',
  secondaryFooterVariant = 'text',
  showSelectionCountInApply = true,
  footerPrimaryAction,
  footerSecondaryAction,
}) => {
  const [internalTab, setInternalTab] = useState(0);
  const tabValue = controlledTabValue !== undefined ? controlledTabValue : internalTab;

  const handleTabChange = (_event: React.SyntheticEvent, v: number) => {
    if (onControlledTabChange) {
      onControlledTabChange(_event, v);
    } else {
      setInternalTab(v);
    }
  };

  const coerceTabIfNeeded = (next: number) => {
    if (onControlledTabChange) {
      onControlledTabChange({} as React.SyntheticEvent, next);
    } else {
      setInternalTab(next);
    }
  };

  const [localSelected, setLocalSelected] = useState<string[]>(selectedFilterValues || []);
  const [internalSearch, setInternalSearch] = useState('');
  const searchTerm = textFilterControlled ? controlledSearchTerm : internalSearch;

  const rangeDraftControlled = Boolean(onRangeMinInputChange && onRangeMaxInputChange);
  const [localMinValue, setLocalMinValue] = useState<number | null>(minValue ?? null);
  const [localMaxValue, setLocalMaxValue] = useState<number | null>(maxValue ?? null);
  const [localStartDate, setLocalStartDate] = useState<Date | null>(dateFilter?.startDate ?? null);
  const [localEndDate, setLocalEndDate] = useState<Date | null>(dateFilter?.endDate ?? null);

  const safeOptions = useMemo(() => sanitizeOptions(filterOptions), [filterOptions]);

  const hasFilterOptions =
    isRangeFilter ||
    isDateFilter ||
    forceShowFilterTab ||
    safeOptions.length > 0 ||
    Boolean(children) ||
    Boolean(customFilterContent);

  const effectiveSelected = textFilterControlled ? controlledSelectedValues : localSelected;
  const menuJustOpenedRef = useRef(false);

  useEffect(() => {
    if (!open && !textFilterControlled) {
      setLocalSelected(selectedFilterValues || []);
    }
  }, [open, selectedFilterValues, textFilterControlled]);

  useEffect(() => {
    if (!rangeDraftControlled) {
      setLocalMinValue(minValue ?? null);
      setLocalMaxValue(maxValue ?? null);
    }
  }, [minValue, maxValue, rangeDraftControlled]);

  useEffect(() => {
    if (open && isDateFilter) {
      setLocalStartDate(dateFilter?.startDate ?? null);
      setLocalEndDate(dateFilter?.endDate ?? null);
    }
  }, [open, isDateFilter, dateFilter?.startDate, dateFilter?.endDate]);

  useEffect(() => {
    if (open && !textFilterControlled) {
      setLocalSelected(selectedFilterValues || []);
      setInternalSearch(initialFilterSearchTerm ?? '');
    }
  }, [open, textFilterControlled, initialFilterSearchTerm, selectedFilterValues]);

  useEffect(() => {
    if (open && !menuJustOpenedRef.current && controlledTabValue === undefined) {
      setInternalTab(0);
    }
    menuJustOpenedRef.current = open;
  }, [open, controlledTabValue]);

  useEffect(() => {
    if (controlledTabValue !== undefined) return;
    if (!isSortable && hasFilterOptions && tabValue !== 0) {
      coerceTabIfNeeded(0);
    } else if (isSortable && !hasFilterOptions && tabValue !== 0) {
      coerceTabIfNeeded(0);
    } else if (isSortable && hasFilterOptions && tabValue > 1) {
      coerceTabIfNeeded(0);
    }
  }, [controlledTabValue, isSortable, hasFilterOptions, tabValue]);

  const filteredOptions = useMemo(() => {
    const base = textFilterControlled ? sanitizeOptions(filterOptions) : safeOptions;
    if (!enableSearch) return base;
    return base.filter((option) => option.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [enableSearch, searchTerm, filterOptions, safeOptions, textFilterControlled]);

  const handleToggle = (value: string) => {
    if (textFilterControlled) {
      onToggleControlledValue?.(value);
      return;
    }
    const currentIndex = localSelected.indexOf(value);
    const next = [...localSelected];
    if (currentIndex === -1) next.push(value);
    else next.splice(currentIndex, 1);
    setLocalSelected(next);
  };

  const effectiveMin = rangeDraftControlled ? minValue ?? null : localMinValue;
  const effectiveMax = rangeDraftControlled ? maxValue ?? null : localMaxValue;

  const runDefaultApply = () => {
    if (isRangeFilter && onRangeFilterApply) {
      onRangeFilterApply(effectiveMin, effectiveMax);
    } else if (isDateFilter && onDateFilterApply) {
      onDateFilterApply(localStartDate, localEndDate);
    } else if (onFilterApply) {
      if (children || customFilterContent) {
        onFilterApply([]);
      } else {
        onFilterApply(
          localSelected,
          enableSearch ? { searchTerm: internalSearch } : undefined
        );
      }
    }
    onClose();
  };

  const handlePrimaryFooter = () => {
    if (footerPrimaryAction) {
      footerPrimaryAction();
      return;
    }
    runDefaultApply();
  };

  const handleSecondaryFooter = () => {
    if (footerSecondaryAction) {
      footerSecondaryAction();
      return;
    }
    onClose();
  };

  const handleFilterClear = () => {
    if (textFilterControlled) {
      onControlledClearSelections?.();
      return;
    }
    if (isRangeFilter) {
      if (rangeDraftControlled) {
        onRangeMinInputChange?.(null);
        onRangeMaxInputChange?.(null);
      } else {
        setLocalMinValue(null);
        setLocalMaxValue(null);
      }
    } else if (isDateFilter) {
      setLocalStartDate(null);
      setLocalEndDate(null);
    } else {
      setLocalSelected([]);
    }
  };

  const handleFilterSelectAll = () => {
    if (textFilterControlled) {
      onControlledSelectAll?.();
      return;
    }
    const optionsToSelect =
      enableSearch && searchTerm ? filteredOptions : safeOptions.length ? safeOptions : filteredOptions;
    setLocalSelected([...optionsToSelect]);
  };

  const handleSortClick = (order: SortDirection) => {
    if (sortField) {
      onSort(sortField, order);
      onClose();
    }
  };

  const isCurrentlySorted = isSortable && sortField && currentSortField === sortField;

  const hasTabs = isSortable || hasFilterOptions;
  const hasMultipleTabs = isSortable && hasFilterOptions;

  if (!hasTabs) {
    return null;
  }

  const showAllButton =
    !isRangeFilter && !isDateFilter && !customFilterContent && !omitAllClearHeader;
  const applyCountSuffix =
    showSelectionCountInApply && !footerPrimaryAction && !isRangeFilter && !isDateFilter && !children && !customFilterContent
      ? ` (${effectiveSelected.length})`
      : '';

  const rangeMinLabel = numericRangeInputStyle === 'greaterLess' ? 'Greater Than (>)' : 'Min';
  const rangeMaxLabel = numericRangeInputStyle === 'greaterLess' ? 'Less Than (<)' : 'Max';

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      PaperProps={{
        sx: columnFilterMenuPaperSx,
        /** Avoid parent handlers (e.g. table header sort on `TableCell`) seeing this mousedown. */
        onMouseDown: (e: React.MouseEvent) => {
          e.stopPropagation();
        },
      }}
      MenuListProps={{ sx: columnFilterMenuListSx }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {hasMultipleTabs && (
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{ ...columnFilterTabsSx, px: 0.75 }}
          >
            {hasFilterOptions && <Tab label="Filter" sx={columnFilterTabSx} />}
            {isSortable && <Tab label="Sort" sx={columnFilterTabSx} />}
          </Tabs>
        )}

        {hasFilterOptions && tabValue === 0 && (
          <Box
            sx={{
              p: 1,
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            {!hideStandardFilterHeader && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 0.5,
                    mb: 0.75,
                    flexShrink: 0,
                  }}
                >
                  <Typography variant="subtitle2" sx={columnFilterSectionTitleSx}>
                    Filter {title}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                    {showHeaderClearIcon && onHeaderClearFilter && (
                      <IconButton size="small" onClick={onHeaderClearFilter} sx={{ p: 0.35 }}>
                        <ClearIcon sx={{ fontSize: '1rem', color: SEARCE_COLORS.textSecondary }} />
                      </IconButton>
                    )}
                    {!omitAllClearHeader && (
                      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5 }}>
                        {showAllButton && (
                          <Button size="small" onClick={handleFilterSelectAll} sx={columnFilterHeaderActionButtonSx}>
                            All
                          </Button>
                        )}
                        <Button size="small" onClick={handleFilterClear} sx={columnFilterHeaderActionButtonSx}>
                          Clear
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>

                <Divider sx={{ mb: 0.75, flexShrink: 0 }} />
              </>
            )}

            {customFilterContent ? (
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>{customFilterContent}</Box>
            ) : children ? (
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>{children}</Box>
            ) : isDateFilter ? (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <DatePicker
                    label="Start Date"
                    value={localStartDate}
                    onChange={(newValue) => setLocalStartDate(newValue)}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        onKeyDown: (e: React.KeyboardEvent) => e.stopPropagation(),
                        sx: {
                          '& .MuiInputLabel-root': { fontSize: '0.7rem' },
                          '& .MuiInputBase-input': { py: 0.65, fontSize: '0.75rem' },
                        },
                      },
                    }}
                  />
                  <DatePicker
                    label="End Date"
                    value={localEndDate}
                    onChange={(newValue) => setLocalEndDate(newValue)}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        onKeyDown: (e: React.KeyboardEvent) => e.stopPropagation(),
                        sx: {
                          '& .MuiInputLabel-root': { fontSize: '0.7rem' },
                          '& .MuiInputBase-input': { py: 0.65, fontSize: '0.75rem' },
                        },
                      },
                    }}
                  />
                </Box>
              </LocalizationProvider>
            ) : isRangeFilter ? (
              <Box sx={{ flexShrink: 0 }}>
                {numericRangeHint ? (
                  <Typography variant="body2" sx={{ mb: 1, color: SEARCE_COLORS.textSecondary, fontSize: '0.68rem' }}>
                    {numericRangeHint}
                  </Typography>
                ) : null}
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    size="small"
                    label={rangeMinLabel}
                    type="number"
                    value={effectiveMin !== null && effectiveMin !== undefined ? effectiveMin : ''}
                    onChange={(e) => {
                      const value = e.target.value === '' ? null : parseFloat(e.target.value);
                      if (rangeDraftControlled) {
                        onRangeMinInputChange?.(value);
                      } else {
                        setLocalMinValue(value);
                      }
                    }}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={{ flex: 1, '& .MuiInputLabel-root': { fontSize: '0.7rem' } }}
                    inputProps={{ min: rangeMin, max: rangeMax, step: 'any' }}
                    placeholder="Min value"
                  />
                  <TextField
                    size="small"
                    label={rangeMaxLabel}
                    type="number"
                    value={effectiveMax !== null && effectiveMax !== undefined ? effectiveMax : ''}
                    onChange={(e) => {
                      const value = e.target.value === '' ? null : parseFloat(e.target.value);
                      if (rangeDraftControlled) {
                        onRangeMaxInputChange?.(value);
                      } else {
                        setLocalMaxValue(value);
                      }
                    }}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={{ flex: 1, '& .MuiInputLabel-root': { fontSize: '0.7rem' } }}
                    inputProps={{ min: rangeMin, max: rangeMax, step: 'any' }}
                    placeholder="Max value"
                  />
                </Box>
              </Box>
            ) : (
              <>
                {enableSearch && (
                  <Box sx={{ flexShrink: 0 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={`Search ${title.toLowerCase()}...`}
                      value={searchTerm}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (textFilterControlled) {
                          onControlledSearchChange?.(v);
                        } else {
                          setInternalSearch(v);
                        }
                      }}
                      onKeyDown={(e) => e.stopPropagation()}
                      InputProps={{
                        startAdornment: (
                          <SearchIcon sx={{ mr: 1, color: SEARCE_COLORS.textSecondary, fontSize: '1rem' }} />
                        ),
                      }}
                      sx={columnFilterSearchFieldSx}
                    />
                    <Divider sx={{ mb: 1 }} />
                  </Box>
                )}

                <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                  {filteredOptions.length === 0 ? (
                    <Typography variant="body2" sx={columnFilterEmptyTypographySx}>
                      {enableSearch && searchTerm ? 'No results found' : 'No options available'}
                    </Typography>
                  ) : (
                    filteredOptions.map((option) => (
                      <Box
                        key={option}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          py: 0.15,
                          px: 0.5,
                          cursor: 'pointer',
                          borderRadius: 1,
                          '&:hover': { backgroundColor: alpha(SEARCE_COLORS.primary, 0.1) },
                        }}
                        onClick={() => handleToggle(option)}
                      >
                        <input
                          type="checkbox"
                          checked={effectiveSelected.includes(option)}
                          onChange={() => handleToggle(option)}
                          style={{ marginRight: 6, width: 14, height: 14 }}
                        />
                        <Typography variant="body2" sx={columnFilterOptionRowTypographySx}>
                          {option}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
              </>
            )}

            <Divider sx={{ my: 0.75, flexShrink: 0 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.75, flexShrink: 0 }}>
              <Button
                size="small"
                variant={secondaryFooterVariant === 'outlined' ? 'outlined' : 'text'}
                onClick={handleSecondaryFooter}
                sx={columnFilterFooterSecondaryButtonSx}
              >
                {secondaryFooterLabel}
              </Button>
              <Button size="small" variant="contained" onClick={handlePrimaryFooter} sx={columnFilterFooterPrimaryButtonSx}>
                {primaryFooterLabel}
                {applyCountSuffix}
              </Button>
            </Box>
          </Box>
        )}

        {isSortable && (hasFilterOptions ? tabValue === 1 : tabValue === 0) && (
          <Box sx={{ p: 1, flexShrink: 0 }}>
            {sortPanelMode === 'columnSelect' ? (
              <>
                <Typography variant="body2" sx={{ ...columnFilterSortHintSx, fontWeight: 600 }}>
                  Sort {title}
                </Typography>
                <FormControl fullWidth sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={columnFilterCaptionSx}>
                    Sort by column
                  </Typography>
                  <Select
                    size="small"
                    value={sortColumnSelectValue || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      onSortColumnSelectChange?.(value === '' ? null : value);
                    }}
                    displayEmpty
                    sx={{ '& .MuiSelect-select': { py: 0.65, fontSize: '0.75rem' } }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {sortableColumns.map((col) => (
                      <MenuItem key={col.id} value={col.id}>
                        {col.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {sortColumnSelectValue && (
                  <FormControl fullWidth>
                    <Typography variant="caption" sx={columnFilterCaptionSx}>
                      Sort direction
                    </Typography>
                    <RadioGroup
                      row
                      value={sortDirection}
                      onChange={(e) => onSortDirectionChange?.(e.target.value as SortDirection)}
                    >
                      <FormControlLabel
                        value="asc"
                        control={<Radio size="small" />}
                        label="Ascending"
                        sx={{ '& .MuiTypography-root': { fontSize: '0.68rem' } }}
                      />
                      <FormControlLabel
                        value="desc"
                        control={<Radio size="small" />}
                        label="Descending"
                        sx={{ '& .MuiTypography-root': { fontSize: '0.68rem' } }}
                      />
                    </RadioGroup>
                  </FormControl>
                )}
              </>
            ) : (
              <>
                <Typography variant="body2" sx={columnFilterSortHintSx}>
                  Sort by {title}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  <Button
                    size="small"
                    variant={isCurrentlySorted && currentSortOrder === 'asc' ? 'contained' : 'outlined'}
                    onClick={() => handleSortClick('asc')}
                    sx={{
                      ...columnFilterSortAscDescButtonSx,
                      backgroundColor:
                        isCurrentlySorted && currentSortOrder === 'asc' ? SEARCE_COLORS.primary : 'transparent',
                      '&:hover': {
                        backgroundColor:
                          isCurrentlySorted && currentSortOrder === 'asc'
                            ? '#0056cc'
                            : alpha(SEARCE_COLORS.primary, 0.1),
                      },
                    }}
                    startIcon={<ArrowDropUpIcon />}
                  >
                    Sort ascending
                  </Button>
                  <Button
                    size="small"
                    variant={isCurrentlySorted && currentSortOrder === 'desc' ? 'contained' : 'outlined'}
                    onClick={() => handleSortClick('desc')}
                    sx={{
                      ...columnFilterSortAscDescButtonSx,
                      backgroundColor:
                        isCurrentlySorted && currentSortOrder === 'desc' ? SEARCE_COLORS.primary : 'transparent',
                      '&:hover': {
                        backgroundColor:
                          isCurrentlySorted && currentSortOrder === 'desc'
                            ? '#0056cc'
                            : alpha(SEARCE_COLORS.primary, 0.1),
                      },
                    }}
                    startIcon={<ArrowDropDownIcon />}
                  >
                    Sort descending
                  </Button>
                </Box>
              </>
            )}

            {hasMultipleTabs && (
              <>
                <Divider sx={{ my: 0.75 }} />
                <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    variant={secondaryFooterVariant === 'outlined' ? 'outlined' : 'text'}
                    onClick={handleSecondaryFooter}
                    sx={columnFilterFooterSecondaryButtonSx}
                  >
                    {secondaryFooterLabel}
                  </Button>
                  <Button size="small" variant="contained" onClick={handlePrimaryFooter} sx={columnFilterFooterPrimaryButtonSx}>
                    {primaryFooterLabel}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}
      </Box>
    </Menu>
  );
};

export default UnifiedColumnSortFilter;
